import { useCallback, useMemo } from 'react';
import { InlineNotification } from '@carbon/react';
import type { SlotValuationInputsResponse } from 'types/packages-api/slot-valuation-inputs';
import { disclosedSeriesKeys } from './source-blocks';
import { CalculatedResult } from './tearsheet-outputs';
import { ZoneSectionView } from './tearsheet-zone-section';
import {
  buildSectionsWithBlocks,
  editedValueAsString,
  useNodeMeta,
} from './tearsheet-sections';
import { controlForNode } from './editability';
import { EditAfeSourceModalView } from 'pages/packages/show/edit-afe-source-modal/edit-afe-source-modal-view';
import { useAvailableAfes } from '../hooks';
import { EditVolumeSourcePickerModal } from '../edit-volume-source-picker-modal';
import { currentVolumeSourceForNode } from '../volume-source-utils';
import { reasonFixLinks } from 'components/unvalued-reason';
import { EditCostDateModal } from 'pages/packages/show/evaluation-result/edit-cost-date-modal/edit-cost-date-modal';
import { buildOverrideOperations, seriesExtent, slotCategoryOf } from './utils';
import type { SlotOverrideOperation } from '../types';
import type { RowVolumeSourcePair } from '../utils';
import { useTearsheetEdit } from './use-tearsheet-edit';
import styles from './slot-tearsheet.module.scss';

/**
 * The table view of the tearsheet — the `slot_valuation_inputs` payload rendered as
 * the mock's per-zone Parameter/Value tables (each with its scenario / area and
 * formulas), followed by the Calculated result. Paints on the first response (the
 * model-map view is the only thing that waits on the second request — contract
 * note 1).
 *
 * @param {object} props - The tearsheet response.
 * @return {JSX.Element} The table view.
 */
export function TearsheetTableView({
  response,
  slotId,
  packageId,
  onCommitOverride,
  onRerun,
}: {
  response: SlotValuationInputsResponse;
  /** The package the tearsheet belongs to; scopes the volume-source candidates. */
  packageId?: number;
  /** The slot being edited. Absent when the tearsheet is opened without a row identity. */
  slotId?: string | null;
  /**
   * slot-view's `commitSlotOverride`, passed down rather than re-implemented.
   *
   * THE SAME HANDLER THE GRID USES, not a second one. It paints the current page, marks the
   * other pages stale, appends to the working override log and prunes the schedule-dialog
   * channel for the slot+field. Absent on a surface that owns no override log, in which case
   * rows render without an edit control.
   */
  onCommitOverride?: (operation: SlotOverrideOperation) => void;
  /**
   * The route out of the pending state, offered on the "Override pending"
   * badge. Closes the tearsheet and leaves the user at the existing Calculate control.
   * Absent on a surface with no run control to route to, in which case no route is offered.
   */
  onRerun?: () => void;
}) {
  const {
    byKey: meta,
    zones: metaZones,
    resolved: metaResolved,
    failed: metaFailed,
  } = useNodeMeta();
  // ── ⚠️ THE EDIT STATE LIVES IN A HOOK NOW ─────────────────────────────────────────────
  //
  // `editing`, `editingRowLabel`, `beginEdit`, `commitEdit`, `revertEdit`, `commitVolumeEdit`,
  // `dateBounds` and `currentEditingDate` moved to use-tearsheet-edit.ts verbatim — same
  // lines, same order, same dependency arrays. Extracting a hook can change WHEN things run;
  // this one cannot, because the eight were already adjacent and already ran in this order.
  const {
    editing,
    editingRowLabel,
    setEditing,
    beginEdit,
    commitEdit,
    revertEdit,
    commitVolumeEdit,
    dateBounds,
    currentEditingDate,
  } = useTearsheetEdit({ response, meta, slotId, onCommitOverride });

  // Stays in the view: it gates whether the row RENDERS an affordance, which is a rendering
  // decision, not part of the edit state the hook owns.
  const canEdit = Boolean(slotId && onCommitOverride);

  // Built ONCE per payload, not per row: every member of an operation gets
  // the same answer to "what else did this edit set", so computing it per row would rescan
  // every node to reach a result the group already shares.
  const operations = useMemo(
    () => buildOverrideOperations(response.slotInputs ?? []),
    [response.slotInputs]
  );

  // WHICH EDITOR THE OPEN ROW WANTS. Derived from the registry's unit via controlForNode, so
  // the dispatch below has no node-key list of its own to fall out of step with.
  const editingControl = editing ? controlForNode(meta[editing.key]) : null;
  // Candidate AFEs, fetched only while the AFE picker is the open editor. The hook is called
  // unconditionally (rules of hooks) and gated by `enabled`, which is what slot-view's own
  // AfeEditConnector does by mounting the connector only while its modal is open.
  const afeCandidates = useAvailableAfes(slotId ?? '', {
    enabled: editingControl === 'afeSource' && Boolean(slotId),
  });

  // The volume editor's row context, memoised: the picker's comboboxes react to the identity
  // of what they are handed, so a fresh object per render must not reach them.
  //
  // The row's own list narrows by its unit, formation and type-curve area, and badges its
  // current source; a type-curve row opens on that area.
  const volumeEditing = editingControl === 'volumeSource' ? editing : null;
  const editedVolumeSourceId =
    editedValueAsString(volumeEditing?.values?.[0]?.value) ?? null;
  const unitTractId = response.header?.slot?.unitTractId ?? null;
  const formationName = response.header?.slot?.formationName ?? null;
  const typeCurveAreaName = response.header?.slot?.typeCurveAreaName ?? null;
  const volumePair = useMemo<RowVolumeSourcePair>(
    () => ({
      unitId: unitTractId,
      formation: formationName,
      typeCurveAreaName,
      resolvedVolumeSourceId: editedVolumeSourceId,
    }),
    [unitTractId, formationName, typeCurveAreaName, editedVolumeSourceId]
  );
  // Pinned into the row's own list when it lacks the source. The tearsheet has no name for it
  // (the option shows the id); its type is the node's `valueSourceType`.
  const forecastStatus = response.header?.slot?.forecastStatus;
  const volumeCurrentSource = useMemo(
    () =>
      volumeEditing
        ? currentVolumeSourceForNode(volumeEditing, forecastStatus)
        : undefined,
    [volumeEditing, forecastStatus]
  );

  // DEPENDS ON `meta`, so the marker appears when the model map's second request lands
  // rather than never. `overridable` rides the node definition, not the slot row.
  // The source blocks come back with the sections so the disclosed-series set below reuses
  // them rather than walking the model graph a second time.
  const { sections, blocks } = useMemo(
    // `metaResolved` explicitly: this caller holds the authoritative "the map landed" flag,
    // and the volume-source classification must not be answered without it.
    () => buildSectionsWithBlocks(response, meta, metaZones, metaResolved),
    [response, meta, metaZones, metaResolved]
  );
  // AC 4: the slot's own category decides which of the twelve base discount rates are
  // emphasised. Read from the slot_category NODE — header.slot carries no category.
  const slotCategory = useMemo(
    () => slotCategoryOf(response.slotInputs ?? []),
    [response]
  );
  // From the FIRST response, so an output row's formula paints without waiting on the map.
  const formulaByKey = useMemo(() => {
    const out = new Map<string, string | null>();
    for (const node of response.slotInputs ?? []) {
      out.set(node.key, node.formula);
    }
    return out;
  }, [response]);
  const formulaFor = useCallback(
    (key: string) => formulaByKey.get(key) ?? null,
    [formulaByKey]
  );

  // Once per payload, not per node: every monthly node shares the one series, so its
  // extent is a property of the response rather than of the row.
  //
  // ── ⚠️ MEMOISED, AND THE DEPENDENCY IS THE ROWS ───────────────────────────────────────
  //
  // "`seriesExtent`, `buildSourceBlocks` and `disclosedSeriesKeys` still run on every render
  // without `useMemo`." They do not now. All three ran on every keystroke of an inline edit,
  // and `buildSourceBlocks` walks the model graph.
  //
  // DEPS: the rows array alone. `seriesExtent` reads nothing else — not the response, not the
  // registry — so depending on `response` would recompute whenever any other part of the
  // payload changed, which is the decorative-memo failure. The array is a stable reference
  // for the life of a response: React Query hands back the same parsed object until the
  // query key changes, and this component never rewrites it.
  const seriesRows = response.outputs?.cashflowSeries?.rows;
  const extent = useMemo(() => seriesExtent(seriesRows), [seriesRows]);

  // Computed once for the page: a node's inline preview is dropped only where its full
  // series is on the page as a disclosure column. `disclosedSeriesKeys` reads only each
  // block's zone, which does not depend on the model map, so the blocks buildSections
  // produced are the right input.
  const disclosed = useMemo(
    () => disclosedSeriesKeys(blocks, response),
    [blocks, response]
  );

  return (
    <div className={styles.tableView}>
      {/* ── THE MODEL MAP FAILED TERMINALLY, SO THE TABLE PAINTS WITHOUT IT ────────────
          The map is a second request; while it may still arrive the table renders exactly
          as it would from the tearsheet response alone (contract note 1). A terminal
          failure is different and has to be said: units, node labels and the Excel export
          all ride on it, so the reader is told rather than left with numbers missing their
          units and a disabled Export with no reason. */}
      {metaFailed && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="Model map unavailable"
          subtitle="Units, node labels and the Excel export could not load, so some labels fall back to their keys. Reopen the tearsheet to try again."
          className={styles.notice}
        />
      )}
      {sections.map((section) => (
        <ZoneSectionView
          key={section.zone}
          section={section}
          meta={meta}
          slotCategory={slotCategory}
          dates={response.dates}
          allNodes={response.slotInputs ?? []}
          capture={response.header.provenanceCapture ?? null}
          extent={extent}
          response={response}
          disclosed={disclosed}
          onEdit={canEdit ? beginEdit : undefined}
          onRerun={onRerun}
          operations={operations}
          onRevert={canEdit ? revertEdit : undefined}
        />
      ))}
      <CalculatedResult
        outputs={response.outputs}
        meta={meta}
        metaResolved={metaResolved}
        metaFailed={metaFailed}
        formulaFor={formulaFor}
        commissionRates={response.header?.commissionRates}
        fixLinks={reasonFixLinks(response.outputs.unvaluedReason, {
          packageId,
          // SERVED hrefs only — never a route built from the slot id. `provenance` is null on
          // a domain the run recorded nothing for, so it is optional-chained too.
          wellHref: response.header?.slot?.wellHref,
          dpsHref: response.domains.find(
            (group) => group.domain === 'development_pace'
          )?.provenance?.scenarioHref,
          landedWithoutFormation: response.outputs.landedWithoutFormation,
          unvaluedReasons: response.outputs.unvaluedReasons,
        })}
      />
      {/* THE ESTABLISHED SURFACE, NOT A NEW ONE. For the date-valued overridable nodes,
          EditCostDateModal is already the app's single-date editor - same Carbon Modal, same
          DatePicker, same "Slot Id | Well Name" label line. Inventing a tearsheet-specific
          modal would give the same job two looks. The AFE and volume-source nodes get their
          own pickers below. */}
      {editing && editingControl === 'date' && (
        <EditCostDateModal
          isOpen
          onClose={() => setEditing(null)}
          onSave={commitEdit}
          slotId={slotId ?? ''}
          wellName={response.header.slot.wellName ?? undefined}
          // THE ROW THE USER CLICKED. `editing.label` is the NODE's name, which is the row's
          // too everywhere except the three scheduled dates — and there it names the row
          // they did not click. Falls back to the node only for a state that cannot occur
          // (beginEdit always sets both), rather than rendering an empty heading if it did.
          dateFieldLabel={editingRowLabel ?? editing.label}
          minDate={dateBounds.min}
          maxDate={dateBounds.max}
          minLabel={dateBounds.minLabel}
          maxLabel={dateBounds.maxLabel}
          // ── ⚠️ THE MERGED DATE, NOT THE SCHEDULED NODE ───────────────────────────────
          //
          // This read `editing.values[0]` — the node's own value, which for the three
          // scheduled milestones is what ScheduledDatesStep wrote from the pace scenario
          // WITHOUT consulting overrides. So after an override the picker opened on the
          // superseded date, and on a slot whose scheduled node is empty it opened blank.
          //
          // `response.dates[milestone].value` is the merged, effective date the row itself
          // displays — the number the user is looking at when they click Edit. Falls back to
          // the node only for a node with no milestone (the AFE dates), where they are the
          // same value anyway.
          currentDate={currentEditingDate}
        />
      )}
      {/* THE SAME WRITE PATH, A DIFFERENT PICKER. `commitEdit` already resolves the key through
          overrideFieldKeyFor, which maps afe_header_id to `afeSourceId` — the spelling
          AFE_ID_KEYS accepts and the one slot-view's own AFE modal writes. So the AFE save is
          commitEdit with the chosen id, not a second write path. */}
      {editing && editingControl === 'afeSource' && slotId && (
        <EditAfeSourceModalView
          isOpen
          onClose={() => setEditing(null)}
          // The well name and catalog href travel with the id, for the reason the grid's own
          // AFE modal sends them: the two columns are painted from the operation, not
          // refetched, so an id without them leaves the new AFE beside the old AFE's name.
          onSave={(afeId, afeHref, _costs, afeWellName) =>
            commitEdit(afeId, { afeWellName, afeSourceHref: afeHref })
          }
          slotId={slotId}
          wellName={response.header.slot.wellName ?? undefined}
          currentAfeSourceId={editedValueAsString(editing.values?.[0]?.value)}
          data={afeCandidates.data ?? null}
          isLoading={afeCandidates.isPending}
          error={afeCandidates.error?.message ?? null}
        />
      )}
      {editing && editingControl === 'volumeSource' && slotId && (
        <EditVolumeSourcePickerModal
          onClose={() => setEditing(null)}
          onSave={commitVolumeEdit}
          slotId={slotId}
          wellName={response.header.slot.wellName ?? undefined}
          isWellSlot={response.header.isWellSlot}
          currentVolumeSourceId={editedVolumeSourceId ?? undefined}
          pair={volumePair}
          packageId={packageId}
          currentSource={volumeCurrentSource}
        />
      )}
    </div>
  );
}
